/**
 * El estado de una tabla, vivo en la URL.
 *
 * ERP-UX.md §3 lo pide como requisito no negociable: «filtros y orden en la
 * query string → enlaces compartibles». No es un capricho técnico. En una
 * empresa de mantenimiento, «los equipos vencidos de Mallorca» se pega en
 * un correo, se guarda en favoritos y se abre el lunes siguiente; si el
 * estado vive en memoria, esa frase no se puede compartir y cada persona
 * la reconstruye a mano, mal.
 *
 * Reglas de este módulo:
 *
 * - **Ida y vuelta sin pérdidas.** Lo que se codifica se decodifica igual,
 *   incluidos valores con comas, dos puntos y acentos.
 * - **Una URL manipulada no rompe la pantalla.** Llega de fuera: puede
 *   traer una columna que no existe, un operador inventado o basura. Se
 *   descarta lo que no se entiende y se sigue.
 * - **Lo que está por defecto no se escribe.** Una URL limpia es la que se
 *   comparte; si cada tabla arrastrara sus veinte valores por omisión,
 *   nadie distinguiría lo que de verdad ha filtrado.
 */

export type FilterOperator = 'contains' | 'eq' | 'between' | 'empty' | 'notEmpty';

/** Operadores que no llevan valor: preguntan por la ausencia del dato. */
const VALUELESS: ReadonlySet<FilterOperator> = new Set<FilterOperator>(['empty', 'notEmpty']);

const OPERATORS: ReadonlySet<string> = new Set<FilterOperator>([
  'contains',
  'eq',
  'between',
  'empty',
  'notEmpty',
]);

export interface ColumnFilter {
  operator: FilterOperator;
  /** `between` usa dos; `empty` y `notEmpty`, ninguno. */
  values: string[];
}

export type SortDirection = 'asc' | 'desc';

export interface TableSort {
  column: string;
  direction: SortDirection;
}

export type Density = 'compact' | 'comfortable';

export interface TableState {
  /** Búsqueda libre; la resuelve el servidor. */
  search: string;
  filters: Record<string, ColumnFilter>;
  sort: TableSort | null;
  /** Columnas ocultas. Se guarda lo oculto, no lo visible: al añadir una
   *  columna nueva al ERP, aparece para todos en vez de esconderse para
   *  quien tuviera una vista guardada. */
  hiddenColumns: string[];
  density: Density;
}

export const DEFAULT_TABLE_STATE: TableState = {
  search: '',
  filters: {},
  sort: null,
  hiddenColumns: [],
  density: 'compact',
};

const PARAM = {
  search: 'q',
  sort: 'sort',
  hidden: 'hide',
  density: 'd',
  /** Prefijo de los filtros por columna: `f.status=eq:ACTIVO`. */
  filterPrefix: 'f.',
} as const;

/** ¿Es un identificador de columna razonable? */
function isColumnId(value: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(value);
}

/**
 * Escribe el estado en parámetros de URL.
 *
 * Sólo aparece lo que se aparta de lo normal: una tabla recién abierta
 * produce una URL sin parámetros.
 */
export function encodeTableState(state: TableState): URLSearchParams {
  const params = new URLSearchParams();

  if (state.search.trim()) params.set(PARAM.search, state.search.trim());

  for (const [column, filter] of Object.entries(state.filters)) {
    if (!isColumnId(column)) continue;
    // Los valores se codifican de uno en uno: así una razón social con
    // comas —«Hotelera Ejemplo, S.L.»— sobrevive al viaje.
    const encoded = VALUELESS.has(filter.operator)
      ? filter.operator
      : `${filter.operator}:${filter.values.map(encodeURIComponent).join(',')}`;
    params.set(`${PARAM.filterPrefix}${column}`, encoded);
  }

  if (state.sort) {
    params.set(PARAM.sort, `${state.sort.direction === 'desc' ? '-' : ''}${state.sort.column}`);
  }

  if (state.hiddenColumns.length > 0) {
    params.set(PARAM.hidden, [...state.hiddenColumns].sort().join(','));
  }

  if (state.density !== DEFAULT_TABLE_STATE.density) {
    params.set(PARAM.density, state.density);
  }

  return params;
}

/**
 * Lee el estado de la URL.
 *
 * `knownColumns` no es opcional por comodidad: sin él, un enlace con
 * `?f.contraseña=...` crearía un filtro sobre una columna inexistente que
 * la tabla arrastraría sin poder enseñar ni quitar.
 */
export function decodeTableState(
  params: URLSearchParams,
  knownColumns: readonly string[],
): TableState {
  const known = new Set(knownColumns);
  const state: TableState = {
    ...DEFAULT_TABLE_STATE,
    filters: {},
    hiddenColumns: [],
  };

  state.search = params.get(PARAM.search)?.trim() ?? '';

  for (const [key, raw] of params.entries()) {
    if (!key.startsWith(PARAM.filterPrefix)) continue;

    const column = key.slice(PARAM.filterPrefix.length);
    if (!known.has(column)) continue;

    const filter = parseFilter(raw);
    if (filter) state.filters[column] = filter;
  }

  const sortRaw = params.get(PARAM.sort);
  if (sortRaw) {
    const direction: SortDirection = sortRaw.startsWith('-') ? 'desc' : 'asc';
    const column = sortRaw.replace(/^-/, '');
    if (known.has(column)) state.sort = { column, direction };
  }

  const hidden = params.get(PARAM.hidden);
  if (hidden) {
    state.hiddenColumns = [...new Set(hidden.split(',').filter((c) => known.has(c)))].sort();
  }

  const density = params.get(PARAM.density);
  if (density === 'comfortable' || density === 'compact') state.density = density;

  return state;
}

function parseFilter(raw: string): ColumnFilter | null {
  const separator = raw.indexOf(':');

  if (separator === -1) {
    // Sin valor: sólo tiene sentido para los operadores de ausencia.
    return OPERATORS.has(raw) && VALUELESS.has(raw as FilterOperator)
      ? { operator: raw as FilterOperator, values: [] }
      : null;
  }

  const operator = raw.slice(0, separator);
  if (!OPERATORS.has(operator) || VALUELESS.has(operator as FilterOperator)) return null;

  const values = raw
    .slice(separator + 1)
    .split(',')
    .map(safeDecode)
    .filter((v) => v !== '');

  if (values.length === 0) return null;
  // `between` sin sus dos extremos no es un rango: es medio filtro, y
  // aplicarlo a medias daría un resultado que nadie pidió.
  if (operator === 'between' && values.length !== 2) return null;

  return { operator: operator as FilterOperator, values };
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // `%E0%A4%A` y demás basura: se descarta el valor, no la pantalla.
    return '';
  }
}

/**
 * Descripción en lenguaje humano de los filtros activos.
 *
 * Se enseña encima de la tabla porque el fallo más caro de una tabla
 * filtrada no es filtrar mal: es **olvidar que está filtrada** y concluir
 * que sólo hay tres facturas vencidas.
 */
export function describeFilters(
  state: TableState,
  labelOf: (column: string) => string,
): string[] {
  const parts: string[] = [];

  if (state.search.trim()) parts.push(`Búsqueda: «${state.search.trim()}»`);

  for (const [column, filter] of Object.entries(state.filters)) {
    const label = labelOf(column);
    switch (filter.operator) {
      case 'contains':
        parts.push(`${label} contiene «${filter.values.join(', ')}»`);
        break;
      case 'eq':
        parts.push(`${label}: ${filter.values.join(' o ')}`);
        break;
      case 'between':
        parts.push(`${label} entre ${filter.values[0]} y ${filter.values[1]}`);
        break;
      case 'empty':
        parts.push(`${label} vacío`);
        break;
      case 'notEmpty':
        parts.push(`${label} con valor`);
        break;
    }
  }

  return parts;
}

/** ¿Hay algo activo que merezca un botón de «quitar filtros»? */
export function isFiltered(state: TableState): boolean {
  return state.search.trim() !== '' || Object.keys(state.filters).length > 0;
}
